import type { ReactNode } from 'react';
import { DetailLink } from '../components';
import type { OperationUser } from './types';

/**
 * Left-hand card of the Context row — user identity + a few key-value
 * rows that the caller supplies as children (grade / proficiency / pipeline
 * config / previous hints — whatever the operation cares about).
 *
 * See docs/FeatureDesigns/operation-detail-unified-layout.md.
 */
export interface UserProfileCardProps {
    user?: OperationUser;
    /** Called when the user-link is clicked. Receives the resolved user id. */
    onViewUser?: (userId: string) => void;
    /** Zero or more <DetailItem /> children to render under the primary row. */
    children?: ReactNode;
}

export function UserProfileCard({ user, onViewUser, children }: UserProfileCardProps) {
    return (
        <div className="card user-card">
            <h3>
                User Profile{' '}
                {user?.id && (
                    <DetailLink
                        href={`/users/${user.id}`}
                        title="View user"
                        onClick={e => {
                            e.preventDefault();
                            onViewUser?.(user.id!);
                        }}
                    />
                )}
            </h3>
            <div className="user-info">
                <div className="user-primary">
                    <span className="user-name">{user?.name || 'Unknown'}</span>
                    <span className="user-email">{user?.email || '-'}</span>
                </div>
                <div className="user-details">
                    <DetailItem label="Grade">
                        {user?.grade ? `Grade ${user.grade}` : '-'}
                    </DetailItem>
                    {children}
                    {user?.pipelineConfig && (
                        <DetailItem label="Pipeline">
                            <span
                                className={`pipeline-config-badge ${user.pipelineConfig === 'default' ? '' : 'custom'}`.trim()}
                                title="Pipeline config used at submission time"
                            >
                                {user.pipelineConfig}
                            </span>
                        </DetailItem>
                    )}
                </div>
            </div>
        </div>
    );
}

export interface DetailItemProps {
    label: string;
    children: ReactNode;
}

/** Standard label/value row used inside UserProfileCard and MetadataCard. */
export function DetailItem({ label, children }: DetailItemProps) {
    return (
        <div className="detail-item">
            <label>{label}</label>
            {typeof children === 'string' ? <span>{children}</span> : children}
        </div>
    );
}
